import type { ApiErrorBody } from "./types"

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || "http://127.0.0.1:8000").replace(
  /\/$/,
  "",
)

export function apiUrl(path: string): string {
  return `${API_BASE_URL}${path}`
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string | null
  readonly retryable: boolean

  constructor(status: number, body: ApiErrorBody) {
    const message = body.error?.message || formatApiDetail(body.detail) || `请求失败 (${status})`
    super(message)
    this.name = "ApiError"
    this.status = status
    this.code = body.error?.code || null
    this.retryable = body.error?.retryable ?? false
  }
}

function formatApiDetail(detail: unknown): string {
  if (typeof detail === "string") return detail
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === "string") return item
        if (!item || typeof item !== "object") return String(item)
        const record = item as Record<string, unknown>
        const location = Array.isArray(record.loc)
          ? record.loc.filter((part) => part !== "query" && part !== "body").join(".")
          : ""
        const message = typeof record.msg === "string" ? record.msg : "参数格式不正确"
        return location ? `${location}: ${message}` : message
      })
      .filter(Boolean)
      .join("；")
  }
  if (detail && typeof detail === "object") {
    const message = (detail as Record<string, unknown>).message
    if (typeof message === "string") return message
  }
  return ""
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown
  csrfToken?: string
  apiKey?: string
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers = new Headers(options.headers)
  if (options.body !== undefined) headers.set("Content-Type", "application/json")
  if (options.csrfToken) headers.set("X-CSRF-Token", options.csrfToken)
  if (options.apiKey) headers.set("Authorization", `Bearer ${options.apiKey}`)

  const response = await fetch(apiUrl(path), {
    ...options,
    credentials: "include",
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  })
  if (response.status === 204) return undefined as T
  const body = (await response.json().catch(() => ({}))) as T | ApiErrorBody
  if (!response.ok) throw new ApiError(response.status, body as ApiErrorBody)
  return body as T
}
